import { formatSalaryRange, getRmcCategories } from '@components/common';

interface IFaqOptions {
  hideCompanyName?: boolean;
}

// Generate FAQ items for both schema and component display
export const generateFaqItems = (
  jobDetails: any,
  options: IFaqOptions = {}
) => {
  const { hideCompanyName = false } = options;
  const formatListWithAnd = (values: any): string => {
    const items = (Array.isArray(values) ? values : [values])
      .map((value) => `${value}`.trim())
      .filter(Boolean);

    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  };

  const jobTitle = jobDetails?.title || 'this';
  const companyName = hideCompanyName ? '' : jobDetails?.company?.name || '';
  const remoteWorkLevel = jobDetails?.remoteOptions?.[0] || '';
  let jobSchedule = '';
  if (
    Array.isArray(jobDetails?.jobSchedules) &&
    jobDetails.jobSchedules.length > 0
  ) {
    jobSchedule = jobDetails.jobSchedules.join(', ');
  } else if (typeof jobDetails?.jobSchedules === 'string') {
    jobSchedule = jobDetails.jobSchedules;
  }
  const benefits = formatListWithAnd(jobDetails?.jobBenefits) || '';
  const salaryRange = jobDetails?.salaryRange || null;

  // Properly extract category names from objects using getRmcCategories
  const rmcCategories = getRmcCategories(jobDetails?.categories || []);
  const categories =
    rmcCategories
      ?.map((cat: any) => cat?.name || cat?.title || '')
      .filter((name: string) => name)
      .join(', ') || '';

  // Build comprehensive location string
  let location = '';
  if (typeof jobDetails?.jobLocations === 'string' && jobDetails.jobLocations) {
    location = jobDetails.jobLocations;
  } else if (
    Array.isArray(jobDetails?.jobLocations) &&
    jobDetails.jobLocations.length > 0
  ) {
    location = jobDetails.jobLocations.join(', ');
  } else {
    const locationParts: string[] = [];
    if (Array.isArray(jobDetails?.cities) && jobDetails.cities.length > 0) {
      locationParts.push(...jobDetails.cities);
    }
    if (Array.isArray(jobDetails?.states) && jobDetails.states.length > 0) {
      locationParts.push(...jobDetails.states);
    }
    if (locationParts.length > 0) {
      location = locationParts.join(', ');
    } else if (jobDetails?.location) {
      location = jobDetails.location;
    }
  }

  const rawEducationLevel = Array.isArray(jobDetails?.educationLevels)
    ? jobDetails.educationLevels[0]
    : jobDetails?.educationLevels || null;
  const educationLevel =
    rawEducationLevel?.toLowerCase() === 'no education specified'
      ? null
      : rawEducationLevel;
  const faqItems: Array<{ question: string; answer: string }> = [];

  // Add remote work question
  if (remoteWorkLevel) {
    faqItems.push({
      question: `Does this ${jobTitle} job${companyName ? ' at ' + companyName : ''
        } provide an option for remote work?`,
      answer: `This job offers ${remoteWorkLevel}.`,
    });
  }

  // Add job schedule question
  if (jobSchedule) {
    faqItems.push({
      question: `What is the employment classification for this ${jobTitle} role?`,
      answer: jobSchedule,
    });
  }

  // Add benefits question if benefits are available
  if (jobDetails?.jobBenefits && jobDetails.jobBenefits.length > 0) {
    faqItems.push({
      question: `Are there any other employee benefits offered for this ${jobTitle} role${companyName ? ' at ' + companyName : ''
        }?`,
      answer: `Yes, the benefits include ${benefits}.`,
    });
  }

  // Add salary question
  faqItems.push({
    question: `What is the pay range associated with this ${jobTitle} role?`,
    answer:
      salaryRange
        ? formatSalaryRange(salaryRange)
        : "<i>This job posting doesn't provide any salary details at the moment.</i>",
  });

  // Add industries question
  if (categories) {
    faqItems.push({
      question: `To which major industries does this ${jobTitle} position belong?`,
      answer: categories,
    });
  }

  // Add application submission question
  faqItems.push({
    question: `How do I submit my application for the ${jobTitle} position${companyName ? ' at ' + companyName : ''
      }?`,
    answer: 'You can apply directly using the apply button given on the page.',
  });

  // Add eligibility question for remote jobs
  if (jobDetails?.remoteOptions && jobDetails.remoteOptions.length > 0) {
    const countries = jobDetails?.countries;
    faqItems.push({
      question: 'Who is eligible to apply for this job (only for remote jobs)?',
      answer: `Residents of ${location} ${countries.length ? 'or ' + countries : ''
        }`,
    });
  }

  // Add work location question
  if (location) {
    faqItems.push({
      question: `What is the work location for the ${jobTitle} position?`,
      answer: `The work location for this position will be ${location}`,
    });
  }

  // Add education question (always shown; fallback text when education level is not available)
  faqItems.push({
    question: `What academic background is expected for the ${jobTitle} role${companyName ? ' at ' + companyName : ''
      }?`,
    answer: educationLevel
      ? `The required education level for this role is ${educationLevel}`
      : '<i>The employer has not disclosed any minimum education requirements for this job</i>',
  });

  // Add travel requirement question only when answer is meaningful
  const travelAnswerRaw = jobDetails?.travelRequired ?? jobDetails?.travel;
  const travelAnswer = `${travelAnswerRaw ?? ''}`.trim();
  const shouldShowTravelFaq =
    travelAnswer.length > 0 &&
    travelAnswer.toLowerCase() !== 'no specification';

  if (shouldShowTravelFaq) {
    faqItems.push({
      question: `Is travel required for this ${jobTitle} role${companyName ? ' at ' + companyName : ''
        }?`,
      answer: travelAnswer,
    });
  }

  return faqItems;
};

// Create FAQ Schema for SEO
export const createFaqSchema = (jobDetails: any, options: IFaqOptions = {}) => {
  const faqItems = generateFaqItems(jobDetails, options);
  // Strip HTML tags for schema (plain text only)
  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  // Convert to schema.org format
  const schemaItems = faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripHtml(item.answer),
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: schemaItems,
  };
};
